"use client";
import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import { select, label, selectHeading, icons } from './styles.js';
import { useOutsideClickClose } from '../utils/hooks/useOutsideClickClose.web.js';
import { useBoolean } from '../utils/hooks/useBoolean.js';
import { ThemeProvider } from 'styled-components';
import { ErrorMessage } from '../ErrorMessage/ErrorMessage.web.js';
import { ContentText } from '../ContentText/ContentText.web.js';
import { Box } from '../Box/Box.web.js';
import { useSelectAll, draftEvent, joinObjectArrayProperty } from './utils.js';
import { IconChevronDown } from '../icons/ui_navigation/IconChevronDown.web.js';
import { useTheme } from '../utils/theming/useTheme.js';
import { useComponentVariant } from '../utils/hooks/useComponentVariant.js';
import { useSmartControlledEventHandler } from '../utils/hooks/useSmartControlledEventHandler.web.js';
import { callOptional } from '../utils/helpers/function.js';
import { ListItems } from '../utils/components/ListItems/ListItems.web.js';

var _excluded = ["closeAfterClick", "placeholder", "showItems", "selected", "onChange", "disabled", "id", "items", "size", "label", "name", "readOnly", "color", "required", "onBlur", "borderStyle", "boxShadow", "error", "errorMessage", "errorMessageProps", "marginBottom", "mv", "marginVertical", "m", "margin", "multiselect", "displayValue", "renderItem", "selectAll", "variant"];
/**
 * Select components are used in situations where the user needs to select one
 * option from several alternatives. You should use radio button instead if
 * there are only a few options and enough screen space.
 */
function Select(props) {
  var closeAfterClickProp = props.closeAfterClick,
    _props$placeholder = props.placeholder,
    placeholder = _props$placeholder === undefined ? 'Select option' : _props$placeholder,
    _props$showItems = props.showItems,
    showItems = _props$showItems === undefined ? 5 : _props$showItems,
    selectedProp = props.selected,
    onChange = props.onChange,
    disabled = props.disabled,
    id = props.id,
    itemsProp = props.items,
    sizeProp = props.size,
    label$1 = props.label,
    name = props.name,
    readOnly = props.readOnly,
    color = props.color,
    required = props.required,
    onBlur = props.onBlur,
    borderStyleProp = props.borderStyle,
    boxShadow = props.boxShadow,
    error = props.error,
    errorMessage = props.errorMessage,
    errorMessageProps = props.errorMessageProps,
    marginBottomProp = props.marginBottom,
    mvProp = props.mv,
    marginVerticalProp = props.marginVertical,
    mProp = props.m,
    marginProp = props.margin,
    multiselect = props.multiselect,
    displayValue = props.displayValue,
    renderItem = props.renderItem,
    selectAll = props.selectAll,
    variantProp = props.variant,
    rest = _objectWithoutProperties(props, _excluded);
  var _useBoolean = useBoolean(false),
    opened = _useBoolean.value,
    handleClose = _useBoolean.setFalse,
    handleOpen = _useBoolean.setTrue;
  var theme = useTheme();
  var variant = variantProp || theme.variant;
  var size = variant === 'condensed' ? 's' : sizeProp || theme.preferredComponentSize;
  var selectTheme = useComponentVariant(theme.select.web, variant);
  var borderStyle = opened ? 'full' : borderStyleProp || selectTheme.borderStyle;
  var borderRadius = borderStyle === 'bottomOnly' ? 0 : theme.borders.radius;
  var errorMsgRef = React.useRef(null);
  var _React$useState = React.useState(0),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    errorMargin = _React$useState2[0],
    setErrorMargin = _React$useState2[1];
  var _useSmartControlledEv = useSmartControlledEventHandler(multiselect ? [] : null, selectedProp, onChange),
    _useSmartControlledEv2 = _slicedToArray(_useSmartControlledEv, 2),
    selected = _useSmartControlledEv2[0],
    setSelected = _useSmartControlledEv2[1];
  var _useSelectAll = useSelectAll(itemsProp, selected, selectAll),
    items = _useSelectAll.items,
    selectedItems = _useSelectAll.selectedItems,
    headingItems = _useSelectAll.selectedItemsWithoutAll,
    getNewValue = _useSelectAll.getNewValue;
  var closeAfterClick = closeAfterClickProp === undefined ? multiselect ? false : true : closeAfterClickProp;
  React.useEffect(function () {
    if (error) {
      var _errorMsgRef$current$, _errorMsgRef$current;
      // istanbul ignore next -- ?? 0 for sanity
      setErrorMargin((_errorMsgRef$current$ = (_errorMsgRef$current = errorMsgRef.current) === null || _errorMsgRef$current === undefined ? undefined : _errorMsgRef$current.getBoundingClientRect().height) !== null && _errorMsgRef$current$ !== undefined ? _errorMsgRef$current$ : 0);
    } else {
      setErrorMargin(0);
    }
  }, [errorMsgRef, error, errorMessage, errorMessageProps]);
  var buttonRef = React.useRef(null);
  var _useOutsideClickClose = useOutsideClickClose(handleClose),
    wrapperRef = _useOutsideClickClose.wrapperRef;
  var handleCloseFromList = React.useCallback(function () {
    handleClose();
    // istanbul ignore else
    if (buttonRef && buttonRef.current) {
      buttonRef.current.focus();
    }
  }, [buttonRef, handleClose]);
  var handleSelectBlur = React.useCallback(function (e) {
    callOptional(onBlur)(e);

    // Only close if another element received focus, as otherwise there may
    // not be enough time for a click event on one of the ListItems to go
    // through
    if (e.relatedTarget && wrapperRef && wrapperRef.current && !wrapperRef.current.contains(e.relatedTarget)) handleClose();
  }, [handleClose, onBlur, wrapperRef]);
  var handleHeadingClick = React.useCallback(function (e) {
    e.preventDefault();

    // istanbul ignore next: this should never happen, but just to make sure
    if (readOnly) return;
    if (opened) {
      handleClose();
    } else {
      handleOpen();
    }
  }, [readOnly, opened, handleOpen, handleClose]);
  var handleKeyboardTopOut = React.useCallback(function () {
    buttonRef.current && buttonRef.current.focus();
  }, [buttonRef]);
  var handleListInputChange = React.useCallback(function (value) {
    if (closeAfterClick) {
      handleClose();
      buttonRef.current && buttonRef.current.focus();
    }
    var newValue = getNewValue(value);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setSelected(draftEvent(newValue, name));
  }, [closeAfterClick, handleClose, buttonRef, setSelected, getNewValue, name]);
  var headerValue = displayValue ? displayValue : headingItems.length ? joinObjectArrayProperty(headingItems, 'name', renderItem) : placeholder;
  return /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: theme
  }, /*#__PURE__*/React.createElement(select, _extends({
    "data-testid": "elemental-Select-".concat(id || name),
    ref: wrapperRef,
    $opened: opened,
    onBlur: handleSelectBlur,
    id: id
  }, rest, {
    // adapt if any marginal prop is provided
    mv: mvProp || marginVerticalProp,
    m: mProp || marginProp,
    marginBottom: "calc(".concat(errorMargin, "px + ").concat(marginBottomProp || mvProp || marginVerticalProp || marginProp || mProp || '0px', ")")
  }), /*#__PURE__*/React.createElement(label, {
    $opened: opened,
    $readOnly: readOnly,
    $color: color ? color : selectTheme.color,
    $disabled: disabled,
    $size: size
  }, required ? "".concat(label$1, " *") : label$1), /*#__PURE__*/React.createElement(selectHeading, {
    ref: buttonRef,
    "data-testid": "elemental-Select-".concat(id || name, "-button"),
    onClick: handleHeadingClick,
    $opened: opened,
    disabled: readOnly || disabled,
    $disabledStyles: disabled,
    $error: error && !opened // only display error borders if not open
    ,
    type: "button",
    "aria-haspopup": "listbox",
    "aria-controls": "elemental-Select-options-".concat(id || name),
    "aria-expanded": opened,
    "aria-label": "".concat(label$1, ": ").concat(headerValue),
    "aria-invalid": error,
    "aria-describedby": error ? "elemental-Select-".concat(id || name, "__error") : undefined,
    "aria-required": required,
    $readOnly: readOnly,
    $color: color ? color : selectTheme.color,
    $borderRadius: borderRadius,
    $borderStyle: borderStyle
    // this makes shift+tab from opened state to skip button from tab order
    ,
    tabIndex: opened ? -1 : 0,
    $variant: variant,
    $size: size
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      textOverflow: 'ellipsis',
      overflow: 'hidden'
    },
    "data-testid": "elemental-Select-".concat(id || name, "-button-").concat(headingItems.length ? joinObjectArrayProperty(headingItems, 'value', renderItem, '-') : placeholder)
  }, headerValue, !readOnly && /*#__PURE__*/React.createElement(icons, {
    $size: size,
    $borderStyle: borderStyle
  }, /*#__PURE__*/React.createElement(IconChevronDown, {
    size: 24
  }), /*#__PURE__*/React.createElement(IconChevronDown, {
    size: 24
  })))), /*#__PURE__*/React.createElement(Box, {
    hidden: !error
  }, /*#__PURE__*/React.createElement(ErrorMessage, _extends({
    errorRef: errorMsgRef,
    position: "absolute",
    active: true,
    id: "elemental-Select-".concat(id || name, "__error")
  }, errorMessageProps), /*#__PURE__*/React.createElement(ContentText, {
    size: variant === 'condensed' ? 's' : 'm'
  }, errorMessage || 'Please check input'))), !readOnly && !disabled && /*#__PURE__*/React.createElement(ListItems, {
    id: "elemental-Select-options-".concat(id || name),
    color: color ? color : selectTheme.color,
    "aria-label": label$1,
    "aria-multiselectable": multiselect ? true : false,
    highlightSelected: multiselect ? true : false,
    items: items,
    onSelect: handleListInputChange,
    onClose: handleCloseFromList,
    renderItem: renderItem,
    opened: opened,
    selected: multiselect ? selectedItems.map(function (item) {
      return item.value;
    }) : selected,
    activateFirstItem: true,
    showItems: showItems,
    borderRadius: borderRadius,
    enableJumpToLetter: true,
    boxShadow: boxShadow,
    offset: borderStyle === 'bottomOnly' ? 2 : undefined,
    onKeyboardNavigateTopOut: multiselect ? handleKeyboardTopOut : undefined,
    size: size,
    variant: variant
  })));
}

export { Select };
